import { useMemo } from "react";

import { validate as validateCryptoAddress } from "multicoin-address-validator";

import type { CryptoSelectOption } from "../../presentation/CryptoSelect";

/**
 * `true` when the user's current `asset` selection
 * can be preserved if the chain switches to `targetChain`.
 */
export const canKeepAssetOnChain = (
  asset: string,
  targetChain: string,
  assetOptionsByChain: Record<string, CryptoSelectOption[]>,
): boolean =>
  !!asset &&
  asset !== "NATIVE" &&
  (assetOptionsByChain[targetChain] ?? []).some((opt) => opt.value === asset);

export type AddressValidation =
  | { status: "idle" }
  | { status: "valid"; chainName: string }
  | { status: "validator-unavailable"; chainName: string }
  | {
      status: "mismatch";
      matchedChainCode: string;
      matchedChainName: string;
      canKeepAsset: boolean;
      switchTargetLabel: string;
      currentPairLabel: string;
    }
  | { status: "invalid" };

interface UseAddressValidationProps {
  address: string;
  chain: string;
  asset: string;
  chainNameByCode: Record<string, string>;
  assetOptionsByChain: Record<string, CryptoSelectOption[]>;
}

/**
 * - `idle`                  — nothing to validate (no chain or empty address)
 * - `validator-unavailable` — multicoin-address-validator doesn't know this chain
 * - `valid`                 — address validates against the selected chain
 * - `invalid`               — invalid everywhere we can check
 * - `mismatch`              — invalid for selected chain, but matches another supported chain
 */
export const useAddressValidation = ({
  address,
  chain,
  asset,
  chainNameByCode,
  assetOptionsByChain,
}: UseAddressValidationProps): AddressValidation =>
  useMemo<AddressValidation>(() => {
    if (!chain || !address?.trim()) {
      return { status: "idle" };
    }

    const chainName = chainNameByCode[chain] ?? chain;

    try {
      const isValid = validateCryptoAddress(address, chain);
      if (isValid) {
        return { status: "valid", chainName };
      }
    } catch {
      return { status: "validator-unavailable", chainName };
    }

    const matchingChains: string[] = [];
    const candidateChains = Object.keys(assetOptionsByChain).filter(
      (c) => c !== chain,
    );

    for (const candidateChain of candidateChains) {
      try {
        if (validateCryptoAddress(address, candidateChain)) {
          matchingChains.push(candidateChain);
          if (matchingChains.length > 1) {
            // >1 matches → ambiguous, return early
            return { status: "invalid" };
          }
        }
      } catch {
        // validator not registered for this chain; skip
      }
    }

    if (matchingChains.length !== 1) {
      // Zero matches → genuinely invalid. >1 matches → ambiguous
      return { status: "invalid" };
    }

    const matchedChainCode = matchingChains[0];
    const matchedChainName =
      chainNameByCode[matchedChainCode] ?? matchedChainCode;

    const isNativeAsset = asset === "NATIVE";
    const canKeepAsset = canKeepAssetOnChain(
      asset,
      matchedChainCode,
      assetOptionsByChain,
    );

    const assetLabel =
      (assetOptionsByChain[chain] ?? []).find((opt) => opt.value === asset)
        ?.label ?? asset;

    const switchTargetLabel = canKeepAsset
      ? `${assetLabel} - ${matchedChainCode}`
      : matchedChainCode;

    const currentPairLabel =
      !asset || isNativeAsset ? chain : `${assetLabel} - ${chain}`;

    return {
      status: "mismatch",
      matchedChainCode,
      matchedChainName,
      canKeepAsset,
      switchTargetLabel,
      currentPairLabel,
    };
  }, [address, chain, asset, chainNameByCode, assetOptionsByChain]);
